import { Controller } from "@hotwired/stimulus";

import { useResponsive } from "@bryq/employers-src/behaviors/use_responsive";
import { IS_CYPRESS, IS_SANDBOX } from "@bryq/employers-src/settings";
import { getCustomPropertyValue } from "@bryq/employers-src/utils/css";
import Highcharts from "@bryq/employers-src/utils/highcharts";

export default class extends Controller {
  static targets = ["chartContainer"];

  static values = {
    prevalence: Number,
    precision: Number,
    prevalenceSeriesName: String,
    precisionSeriesName: String,
    title: String,
    isVerbose: Boolean,
    isLarge: Boolean,
  };

  connect() {
    useResponsive(this);
  }

  /**
   * Handle the breakpoint change event.
   * Renders the chart based on the current selection for the large breakpoint.
   * @param {Object} breakpoints
   * @param {string} breakpoints.new
   * @param {string} breakpoints.old
   */
  breakpointChange(breakpoints) {
    const isLarge =
      IS_SANDBOX === true ? this.isLargeValue : breakpoints.new !== "x-small";
    this.renderChart(isLarge);
  }

  // eslint-disable-next-line class-methods-use-this
  renderChart(isLarge) {
    const colorAccentBlue60 = getCustomPropertyValue("--color-accent-blue-60");
    const colorAccentBlue20 = getCustomPropertyValue("--color-accent-blue-20");
    const colorNeutral40 = getCustomPropertyValue("--color-neutral-40");
    const colorNeutral60 = getCustomPropertyValue("--color-neutral-60");
    const colorSecondary30 = getCustomPropertyValue("--color-secondary-30");
    const colorError50 = getCustomPropertyValue("--color-error-50");
    const colorNetural80 = getCustomPropertyValue("--color-neutral-80");

    const chartOptions = {
      chart: {
        type: "column",
        style: {
          fontFamily: "inherit",
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      subtitle: {
        text: null,
      },
      legend: {
        align: "left",
        symbolRadius: 2,
      },
      xAxis: {
        categories: [""],
        crosshair: true,
        accessibility: {
          description: "High performers",
        },
        title: {
          useHTML: true,
          text: "",
          style: {
            color: colorNetural80,
          },
        },
      },
      yAxis: {
        min: 0,
        accessibility: {
          description: "Percentage",
        },
        title: {
          text: "",
        },
        floor: 0,
        minRange: 30,
        stackLabels: {
          enabled: true,
          verticalAlign: "bottom",
          crop: false,
          overflow: "none",
        },
        labels: {
          style: {
            color: colorNeutral60,
            fontSize: "12px",
          },
        },
      },
      plotOptions: {
        series: {
          borderWidth: 0,
          animation: IS_CYPRESS === false,
        },
      },
      series: [
        {
          name: this.prevalenceSeriesNameValue,
          data: [this.prevalenceValue * 100],
          marker: {
            symbol: "square",
          },
          type: "column",
          color: colorAccentBlue60,
          dataLabels: {
            enabled: true,
            color: colorNeutral40,
            format: "{y:.0f}%",
            style: {
              textOutline: "none",
              fontSize: "12px",
              fontWeight: "normal",
            },
          },
        },
        {
          name: this.precisionSeriesNameValue,
          data: [this.precisionValue * 100],
          color: colorAccentBlue20,
          type: "column",
          dataLabels: {
            enabled: true,
            color: colorNeutral40,
            format: "{y:.0f}%",
            style: {
              textOutline: "none",
              fontSize: "12px",
              fontWeight: "normal",
            },
          },
        },
      ],
      tooltip: {
        format:
          '<span style="font-size: 0.8em"></span><br/><span style="color:{point.color}">■</span> {point.series.name}: <b>{point.y:.0f}%</b><br/>',
      },
    };

    if (isLarge) {
      chartOptions.chart.height = 294;
      chartOptions.chart.width = 433;

      chartOptions.plotOptions.series.pointWidth = 100;
      chartOptions.plotOptions.series.groupPadding = 0.11;
    } else {
      chartOptions.chart.height = 332;
      chartOptions.chart.width = 305;
      chartOptions.plotOptions.series.pointWidth = 70.4;
      chartOptions.plotOptions.series.groupPadding = 0.085;
    }

    if (this.isVerboseValue && !Number.isNaN(this.precisionValue)) {
      const variationPercentage = Math.round(
        (this.precisionValue / this.prevalenceValue) * 100 - 100,
      );
      const variationSymbol = variationPercentage > 0 ? "▲" : "▼";
      const variationColor =
        variationPercentage > 0 ? colorSecondary30 : colorError50;

      // TODO: i18n - find a way to make this localisable
      chartOptions.xAxis.title.text = `
      <strong>${this.titleValue}</strong><br />
        <span style="color: ${variationColor}"> ${variationSymbol}</span>
        ${Math.abs(variationPercentage)}%
        `;
    }

    Highcharts.chart(this.chartContainerTarget, chartOptions);
  }
}
